/**
 * Full-screen overlay that portals to document.body to avoid z-index stacking.
 * Architectural Context: Pure UI atom. Used as the shell for modals; children render centered.
 *
 * @param isOpen - Whether the overlay is visible
 * @param onClick - Optional handler for backdrop clicks (e.g. dismiss modal)
 * @param portal - When true, renders via ReactDOM.createPortal into document.body
 * @default portal true
 * @default zIndex 50
 *
 * @example
 * <Backdrop isOpen={open} onClick={() => setOpen(false)}>
 *   <div>Modal content</div>
 * </Backdrop>
 */

import React from 'react';
import { createPortal } from 'react-dom';

export interface BackdropProps {
  isOpen: boolean;
  onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
  className?: string;
  zIndex?: number;
  'aria-hidden'?: boolean;
  children?: React.ReactNode;
  portal?: boolean;
}

export const Backdrop: React.FC<BackdropProps> = ({
  isOpen,
  onClick,
  className = '',
  zIndex = 50,
  'aria-hidden': ariaHidden = true,
  children,
  portal = true,
}) => {
  if (!isOpen) return null;
  const content = (
    <div
      className={`fixed inset-0 bg-black/50 flex items-center justify-center p-4 overflow-y-auto ${className}`}
      style={{ zIndex }}
      onClick={onClick}
      aria-hidden={ariaHidden}
    >
      {children}
    </div>
  );
  return portal ? createPortal(content, document.body) : content;
};
